import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink } from "react-router-dom";

import { setAddress } from "~/actions/clinics";
import { remoteLog } from "~/common/logging";
import { Layout } from "~/components/ui/layout";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import {
  Table,
  TableCell,
  TableEmpty,
  TableHead,
  TableLoading,
  TableRow,
} from "~/components/ui/table";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { getClinics, selectClinics, selectClinicsAreLoading } from "~/slices/clinics";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => {
  return {
    clinics: selectClinics(state),
    clinicsAreLoading: selectClinicsAreLoading(state),
  };
};

const mapDispatchToProps = {
  getClinics,
};

type ClinicsListProps = PropsFromRedux & { intl: IntlShape } & {
    /** Render WITHOUT the legacy `<Layout>` chrome, for embedding inside the redesign shell. */
    bareLayout?: boolean;
};

class ClinicsList extends Component<ClinicsListProps> {
  componentDidMount() {
    window.scrollTo(0, 0);
    void this.props.getClinics();
    setDocumentTitle(this.props.intl.formatMessage({ id: "clin_table.header" }));
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "clinic_list");
  }

  render() {
    const { clinics, clinicsAreLoading } = this.props;

    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main" testId="clinics-page">
          <PortletTitle iconClassName="icon-layers">
            <FormattedMessage id="clin_table.header" />
          </PortletTitle>

          <div className="portlet-body">
            <Table>
              <thead>
                <TableRow>
                  <TableHead>#</TableHead>

                  <TableHead>
                    <FormattedMessage id="clin_table.name" />
                  </TableHead>

                  <TableHead>
                    <FormattedMessage id="clin_table.address" />
                  </TableHead>

                  <TableHead>
                    <FormattedMessage id="clin_table.phone" />
                  </TableHead>

                  <TableHead>
                    <FormattedMessage id="clin_table.email" />
                  </TableHead>
                </TableRow>
              </thead>

              <tbody>
                {clinicsAreLoading ? (
                  <TableLoading columns={5} />
                ) : clinics.length == 0 ? (
                  <TableEmpty columns={5}>
                    <FormattedMessage id="clinics_not_found" />
                  </TableEmpty>
                ) : (
                  clinics.map((clinic, i) => (
                    <TableRow key={clinic.clinic_id}>
                      <TableCell>{i + 1}</TableCell>

                      <TableCell>
                        <NavLink to={`/pages/doctor/clinics/${clinic.clinic_id}`}>
                          {clinic.title}
                        </NavLink>
                      </TableCell>

                      <TableCell>{setAddress(clinic)}</TableCell>
                      <TableCell>{clinic.phone}</TableCell>
                      <TableCell>{clinic.email}</TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(ClinicsList));
